/**
 * @module UseNotificationPreferences
 * @package @geenius/notifications-react
 * @description Wraps preference mutation flows for the React notifications
 * variant. It exposes stable helpers for channel toggles, type toggles, and
 * quiet-hour updates.
 */

import type {
  NotificationChannel,
  NotificationPreferences,
  NotificationType,
} from '@geenius/notifications/shared'
import { useCallback } from 'react'

/**
 * Async preference update function consumed by the hook.
 */
export type UpdateNotificationPreferences = (
  channels?: Record<NotificationChannel, boolean>,
  types?: Record<NotificationType, boolean>,
  quietHours?: { start: string; end: string },
) => Promise<void>

/**
 * State returned by {@link useNotificationPreferences}.
 */
export interface UseNotificationPreferencesResult {
  preferences: NotificationPreferences | null | undefined
  isLoading: boolean
  toggleChannel: (channel: NotificationChannel, enabled: boolean) => Promise<void>
  toggleType: (type: NotificationType, enabled: boolean) => Promise<void>
  setQuietHours: (start: string, end: string) => Promise<void>
}

/**
 * Manages async preference updates for the React notifications package.
 *
 * @param preferences Preference document or loading state.
 * @param updatePreferences Optional async update function.
 * @returns Preference state plus helpers for toggles and quiet hours.
 */
export function useNotificationPreferences(
  preferences: NotificationPreferences | null | undefined,
  updatePreferences?: UpdateNotificationPreferences,
): UseNotificationPreferencesResult {
  const isLoading = preferences === undefined

  const toggleChannel = useCallback(
    async (channel: NotificationChannel, enabled: boolean): Promise<void> => {
      if (!preferences || !updatePreferences) {
        return
      }

      await updatePreferences({ ...preferences.channels, [channel]: enabled })
    },
    [preferences, updatePreferences],
  )

  const toggleType = useCallback(
    async (type: NotificationType, enabled: boolean): Promise<void> => {
      if (!preferences || !updatePreferences) {
        return
      }

      await updatePreferences(undefined, { ...preferences.types, [type]: enabled })
    },
    [preferences, updatePreferences],
  )

  const setQuietHours = useCallback(
    async (start: string, end: string): Promise<void> => {
      if (!updatePreferences) {
        return
      }

      await updatePreferences(undefined, undefined, { start, end })
    },
    [updatePreferences],
  )

  return { preferences, isLoading, toggleChannel, toggleType, setQuietHours }
}
